import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { YField, YInput, YSelect, YBtn } from '../../../../components/yool/YoolPrimitives';
import { useSupabaseClient } from '../../../../lib/supabase';
import {
  getPreciosBase,
  assignPolizaToPrecios,
  clearPolizaFromPrecios,
} from '../../../../services/supabase/planesSupabaseService';

const EMPTY_WALLET_CFG = {
  company_phone: '',
  company_whatsapp: '',
  company_email: '',
  help_links: [],
};

export default function PasoPoliza({
  companiaNombre,
  planNombre,
  planId,
  value,
  onChange,
  onRegisterSyncTramos,
}) {
  const set = (name) => (e) => onChange({ [name]: e.target.value });

  // ── Ramo: select + creación inline (portado de ColectivePolizasForm.jsx) ──
  const supa = useSupabaseClient();
  const [ramos, setRamos] = useState([]);
  const [loadingRamos, setLoadingRamos] = useState(true);
  const [showNewRamo, setShowNewRamo] = useState(false);
  const [newRamoName, setNewRamoName] = useState('');
  const [savingRamo, setSavingRamo] = useState(false);

  useEffect(() => {
    let ignore = false;
    setLoadingRamos(true);
    supa
      .from('ramos')
      .select('id, nombre')
      .order('nombre', { ascending: true })
      .then(({ data, error }) => {
        if (ignore) return;
        if (error) {
          console.warn('Error al obtener ramos:', error.message);
          return;
        }
        setRamos(data || []);
      })
      .catch((error) => console.warn('Error al obtener ramos:', error?.message))
      .finally(() => { if (!ignore) setLoadingRamos(false); });
    return () => { ignore = true; };
  }, [supa]);

  const handleCreateRamo = async () => {
    if (!newRamoName.trim()) return;
    try {
      setSavingRamo(true);
      const { data, error } = await supa
        .from('ramos')
        .insert({ nombre: newRamoName.trim() })
        .select('id, nombre')
        .single();
      if (error) throw error;
      setRamos((prev) => [...prev, data].sort((a, b) => a.nombre.localeCompare(b.nombre)));
      onChange({ ramo_id: String(data.id) });
      setNewRamoName('');
      setShowNewRamo(false);
      toast.success('Ramo creado');
    } catch (e) {
      toast.error(e.message || 'Error al crear ramo');
    } finally {
      setSavingRamo(false);
    }
  };

  // ── Tramos de precio del plan (portado de ColectivePolizasForm.jsx) ──
  const [preciosPlan, setPreciosPlan] = useState([]);
  const [loadingPrecios, setLoadingPrecios] = useState(false);
  const tramosSeleccionados = value?.tramos_seleccionados || [];

  const planIdAnteriorRef = useRef(planId);

  useEffect(() => {
    let ignore = false;
    // Si el usuario retrocede y elige otro plan, la selección de tramos del
    // plan anterior ya no aplica — se limpia (igual que ColectivePolizasForm.jsx).
    // El guard `planIdAnteriorRef.current &&` excluye la transición "vacío → id
    // real" (carga inicial en modo edición, mientras el contenedor todavía está
    // resolviendo getPolizaById) — esa NO es un cambio de plan del usuario, y
    // limpiar ahí borraría los tramos ya asignados que el contenedor recién cargó.
    if (planIdAnteriorRef.current && planIdAnteriorRef.current !== planId) {
      onChange({ tramos_seleccionados: [] });
    }
    planIdAnteriorRef.current = planId;
    if (!planId) {
      setPreciosPlan([]);
      return undefined;
    }
    setLoadingPrecios(true);
    getPreciosBase(planId)
      .then((data) => { if (!ignore) setPreciosPlan(data || []); })
      .catch((error) => {
        console.warn('Error cargando tramos de precio del plan:', error?.message);
        if (!ignore) setPreciosPlan([]);
      })
      .finally(() => { if (!ignore) setLoadingPrecios(false); });
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId]);

  const toggleTramo = (precioId) => (e) => {
    const checked = e.target.checked;
    const next = checked
      ? [...tramosSeleccionados, precioId]
      : tramosSeleccionados.filter((pid) => pid !== precioId);
    onChange({ tramos_seleccionados: next });
  };

  // Expone al padre (wizard container, Task 8) una función para sincronizar
  // la asignación de tramos de precio DESPUÉS de guardar la póliza (necesita
  // el id real de la póliza, que solo existe tras createPoliza/updatePoliza).
  // Se re-registra cada vez que cambian los tramos disponibles o la selección,
  // para que el padre siempre invoque una versión con datos frescos.
  useEffect(() => {
    if (typeof onRegisterSyncTramos !== 'function') return;
    const syncTramos = async (polizaId) => {
      if (!polizaId) return;
      const current = preciosPlan || [];
      const currentlyIds = current
        .filter((p) => String(p?.poliza_colectiva_id) === String(polizaId))
        .map((p) => p.id);
      const selected = value?.tramos_seleccionados || [];
      const toAssign = selected.filter((pid) => !currentlyIds.includes(pid));
      const toClear = currentlyIds.filter((pid) => !selected.includes(pid));
      if (toAssign.length > 0) await assignPolizaToPrecios(toAssign, polizaId);
      if (toClear.length > 0) await clearPolizaFromPrecios(toClear);
    };
    onRegisterSyncTramos(syncTramos);
  }, [onRegisterSyncTramos, preciosPlan, value?.tramos_seleccionados]);

  // ── Wallet Pass — Reverso (portado de ColectivePolizasForm.jsx) ──
  const walletCfg = value?.wallet_back_config || EMPTY_WALLET_CFG;
  const helpLinks = walletCfg.help_links || [];
  const setWalletField = (field) => (e) => onChange({ wallet_back_config: { ...walletCfg, [field]: e.target.value } });
  const addHelpLink = () => onChange({
    wallet_back_config: { ...walletCfg, help_links: [...helpLinks, { label: '', url: '' }] },
  });
  const setHelpLink = (i, field) => (e) => {
    const links = [...helpLinks];
    links[i] = { ...links[i], [field]: e.target.value };
    onChange({ wallet_back_config: { ...walletCfg, help_links: links } });
  };
  const removeHelpLink = (i) => onChange({
    wallet_back_config: { ...walletCfg, help_links: helpLinks.filter((_, j) => j !== i) },
  });

  return (
    <div className="y-form-body">
      <div className="y-form-section">
        <div className="y-grid-2">
          <YField label="Compañía"><p style={{ padding: '8px 0' }}>{companiaNombre || '—'}</p></YField>
          <YField label="Plan"><p style={{ padding: '8px 0' }}>{planNombre || '—'}</p></YField>
        </div>
      </div>

      <div className="y-form-section">
        <div className="y-grid-3">
          <YField label="Número Póliza">
            <YInput data-testid="input-numero-poliza" value={value.numero_poliza} onChange={set('numero_poliza')} />
          </YField>
          <YField label="Número Contrato">
            <YInput value={value.numero_contrato} onChange={set('numero_contrato')} />
          </YField>
          <YField label="URL Póliza">
            <YInput value={value.url_poliza} onChange={set('url_poliza')} />
          </YField>
        </div>
      </div>

      <div className="y-form-section">
        <div className="y-grid-3">
          <YField label="Fecha Emisión">
            <YInput type="datetime-local" value={value.fecha_emision} onChange={set('fecha_emision')} />
          </YField>
          <YField label="Fecha Inicio">
            <YInput type="datetime-local" value={value.fecha_inicio} onChange={set('fecha_inicio')} />
          </YField>
          <YField label="Fecha Término">
            <YInput type="datetime-local" value={value.fecha_termino} onChange={set('fecha_termino')} />
          </YField>
        </div>
      </div>

      <div className="y-form-section">
        <div className="y-grid-2">
          <YField label="Ramo">
            <div style={{ display: 'flex', gap: 8 }}>
              <YSelect
                data-testid="select-ramo"
                value={value?.ramo_id || ''}
                onChange={(e) => onChange({ ramo_id: e.target.value })}
                disabled={loadingRamos}
                style={{ flex: 1 }}
              >
                <option value="">{loadingRamos ? 'Cargando...' : 'Selecciona ramo (opcional)'}</option>
                {ramos.map((r) => (
                  <option key={r.id} value={r.id}>{r.nombre}</option>
                ))}
              </YSelect>
              <YBtn
                type="button"
                variant="outline"
                size="sm"
                title="Crear nuevo ramo"
                onClick={() => setShowNewRamo((v) => !v)}
              >
                +
              </YBtn>
            </div>
            {showNewRamo && (
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <YInput
                  data-testid="input-nuevo-ramo"
                  placeholder="Nombre del nuevo ramo"
                  value={newRamoName}
                  onChange={(e) => setNewRamoName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateRamo()}
                  style={{ flex: 1 }}
                  autoFocus
                />
                <YBtn
                  type="button"
                  size="sm"
                  onClick={handleCreateRamo}
                  disabled={savingRamo || !newRamoName.trim()}
                >
                  {savingRamo ? 'Creando...' : 'Crear'}
                </YBtn>
                <YBtn
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => { setShowNewRamo(false); setNewRamoName(''); }}
                >
                  Cancelar
                </YBtn>
              </div>
            )}
          </YField>
          <YField label="Estado">
            <YSelect data-testid="select-estado" value={value.estado} onChange={set('estado')}>
              <option value="vigente">Vigente</option>
              <option value="suspendida">Suspendida</option>
              <option value="terminada">Terminada</option>
              <option value="anulada">Anulada</option>
            </YSelect>
          </YField>
        </div>
      </div>

      {planId ? (
        <div className="y-form-section">
          <YField label="Tramos de precio del plan">
            {loadingPrecios ? (
              <p className="y-field-hint">Cargando tramos...</p>
            ) : preciosPlan.length ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                {preciosPlan.map((precio) => (
                  <label
                    key={precio.id}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}
                  >
                    <input
                      type="checkbox"
                      data-testid={`checkbox-tramo-${precio.id}`}
                      checked={tramosSeleccionados.includes(precio.id)}
                      onChange={toggleTramo(precio.id)}
                    />
                    <span>
                      <strong>Edad:</strong> {precio?.edad_minima ?? precio?.tramo_etario ?? 'N/A'}
                      {precio?.edad_maxima ? `-${precio.edad_maxima}` : ''}
                      <strong style={{ marginLeft: 8 }}>Sexo:</strong> {precio?.sexo ?? 'N/A'}
                      <strong style={{ marginLeft: 8 }}>UF:</strong> {precio?.precio_uf ?? precio?.tasa ?? 'N/A'}
                      <span style={{ marginLeft: 8 }}>{precio?.Activo ? 'Activo' : 'Inactivo'}</span>
                    </span>
                  </label>
                ))}
              </div>
            ) : (
              <p className="y-field-hint">No hay tramos configurados para este plan.</p>
            )}
          </YField>
        </div>
      ) : null}

      <div className="y-form-section">
        <div className="y-grid-3">
          <YField label="Moneda">
            <YInput value={value.moneda} onChange={set('moneda')} placeholder="Ej: UF, CLP" />
          </YField>
          <YField label="Prima Total">
            <YInput type="number" step="0.01" value={value.prima_total} onChange={set('prima_total')} />
          </YField>
        </div>
      </div>

      <div className="y-form-section">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <span className="y-field-label" style={{ fontSize: 13 }}>Wallet Pass — Reverso</span>
          <span className="y-field-hint">Contacto compañía y links de ayuda mostrados al asegurado</span>
        </div>

        <div className="y-grid-3">
          <YField label="Teléfono compañía">
            <YInput
              placeholder="+56 2 2345 6789"
              value={walletCfg.company_phone}
              onChange={setWalletField('company_phone')}
            />
          </YField>
          <YField label="WhatsApp siniestros">
            <YInput
              placeholder="+56 9 8765 4321"
              value={walletCfg.company_whatsapp}
              onChange={setWalletField('company_whatsapp')}
            />
          </YField>
          <YField label="Email siniestros">
            <YInput
              placeholder="siniestros@compania.cl"
              value={walletCfg.company_email}
              onChange={setWalletField('company_email')}
            />
          </YField>
        </div>

        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span className="y-field-label">Links de ayuda</span>
            <YBtn type="button" variant="outline" size="sm" onClick={addHelpLink}>
              + Agregar link
            </YBtn>
          </div>
          {helpLinks.length === 0 && (
            <p className="y-field-hint">
              Sin links configurados (ej: Red de Farmacias, Red Dental, Mapa de Consultas)
            </p>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {helpLinks.map((link, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <YInput
                  placeholder="Nombre (ej: Red de Farmacias)"
                  value={link.label}
                  onChange={setHelpLink(i, 'label')}
                  style={{ flex: 1 }}
                />
                <YInput
                  placeholder="URL (https://...)"
                  value={link.url}
                  onChange={setHelpLink(i, 'url')}
                  style={{ flex: 2 }}
                />
                <YBtn type="button" variant="ghost" size="sm" onClick={() => removeHelpLink(i)}>
                  Eliminar
                </YBtn>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
